import { FaFileExcel, FaFileInvoice, FaGaugeHigh, FaOilCan, FaScaleBalanced, FaUsers } from 'react-icons/fa6';

// Shared wording for Effy Fuel: the home page tiles, the product page and the features page.
// Plain, friendly English only — and only things the app really does today.
export const FUEL_URL = process.env.REACT_APP_FUEL_URL || 'https://fuel.effyventure.com';

export const EFFY_FUEL_PRODUCT = {
  name: 'Effy Fuel',
  path: '/products/effy-fuel',
  featuresPath: '/products/effy-fuel/features',
};

// `text` is the short line (home page tile and features card); `heading`, `description`
// and `points` are the longer story on the product page.
export const FUEL_FEATURES = [
  {
    id: 'shifts', Icon: FaGaugeHigh, label: 'Shift sales',
    title: 'Shift sales, sorted',
    text: 'Type in the meter readings for each machine. Your sales and cash are worked out for you.',
    heading: 'Close every shift without the calculator',
    description: 'Enter the opening and closing meter readings for each machine and Effy Fuel does the rest. No more long sums at the end of a tiring shift.',
    points: [
      'Sales for every machine and every fuel are worked out for you.',
      'Record cash, UPI and card collections in the same place.',
      'Spot the shifts nobody has entered yet, so nothing is forgotten.',
      'Fix a wrong entry later, any time you need to.',
    ],
  },
  {
    id: 'credit', Icon: FaUsers, label: 'Credit customers',
    title: 'Credit customers',
    text: 'See who owes you money and who has paid in advance. Every payment is counted in the right order.',
    heading: 'Know exactly who owes you',
    description: 'Many bunks sell on credit to trucks, farms and regular customers. Keep every fill and every payment in one clear record for each customer.',
    points: [
      'See what each customer owes at a glance.',
      'If a customer paid in advance, that money is used up first.',
      'Add the old dues you already have when you start, so nothing is lost.',
    ],
  },
  {
    id: 'statements', Icon: FaFileInvoice, label: 'Statements',
    title: 'Neat customer statements',
    text: 'Print a clean statement with your logo and your UPI or bank details, so customers know how to pay.',
    heading: 'Send a statement customers understand',
    description: 'Give your credit customers a tidy page that shows what they bought, what they paid and what is still pending.',
    points: [
      'Pick any dates you like, or use the current billing period.',
      'Your bunk name and logo are on top.',
      'Your UPI and bank details are printed, so paying is easy.',
    ],
  },
  {
    id: 'staff', Icon: FaScaleBalanced, label: 'Staff cash',
    title: 'Staff cash check',
    text: 'See how much each salesperson should hand over, and spot any shortage the same day.',
    heading: 'Catch cash differences the same day',
    description: 'Each salesperson is responsible for the cash they collect. Effy Fuel shows what they should hand over, so a shortage is found the same day and not weeks later.',
    points: [
      'See how much each salesperson should hand over after a shift.',
      'Any shortage is noted against that salesperson.',
      'Tiny differences under ₹1 are rounded off for you.',
    ],
  },
  {
    id: 'stock', Icon: FaOilCan, label: 'Stock',
    title: 'Petrol, diesel and oil stock',
    text: 'Your stock goes down as you sell and goes up as you buy. No more guessing what is left.',
    heading: 'Always know what is left in the tank',
    description: 'Keep track of petrol, diesel and your oil and lubricant packs. Stock updates by itself when you sell and when you buy from your suppliers.',
    points: [
      'Fuel and oil stock updates with every shift entry.',
      'Record what you buy from each supplier and print the purchase slip.',
      'Keep track of your day-to-day expenses too.',
    ],
  },
  {
    id: 'reports', Icon: FaFileExcel, label: 'Reports',
    title: 'Daily reports',
    text: 'See the day at a glance on your phone, and download a shift report in Excel whenever you need it.',
    heading: 'See your whole bunk on one screen',
    description: 'Open the dashboard to see how the day went. When your accountant needs the numbers, download them in Excel with one tap.',
    points: [
      'A simple dashboard shows sales, credit and collections.',
      'Download a shift-wise report in Excel any time.',
      'Works on your phone, tablet or computer.',
    ],
  },
];

// Longer list for the features page, grouped the way a bunk owner thinks about the day.
export const FUEL_FEATURE_GROUPS = [
  {
    title: 'Every shift',
    icon: '⛽',
    items: [
      { name: 'Meter readings for every machine', text: 'Enter opening and closing readings and see the sale for each machine.' },
      { name: 'Sales worked out for you', text: 'Fuel litres, rupees and cash are calculated, so you do not need a calculator.' },
      { name: 'Oil and lubricant sales', text: 'Add packs such as pouches and bottles sold in the same shift.' },
      { name: 'Cash, UPI and card', text: 'Record how customers paid and see the totals match.' },
      { name: 'Missing shift check', text: 'Quickly see the shifts that have not been entered yet.' },
      { name: 'Easy corrections', text: 'Open an old shift and fix a mistake whenever you need to.' },
    ],
  },
  {
    title: 'Customers and money',
    icon: '👥',
    items: [
      { name: 'Credit customers', text: 'Keep a clear record of every fill and every payment for each customer.' },
      { name: 'Advance payments', text: 'Money paid in advance is used first, before new credit is added.' },
      { name: 'Old dues', text: 'Add what customers already owe when you start using the app.' },
      { name: 'Printable statements', text: 'A neat statement with your logo and UPI or bank details.' },
    ],
  },
  {
    title: 'Your team',
    icon: '🧑‍🔧',
    items: [
      { name: 'Salesperson list', text: 'Keep everyone who works at your bunk in one place.' },
      { name: 'Cash handed over', text: 'See what each salesperson should hand over after a shift.' },
      { name: 'Shortage tracking', text: 'Any shortage is noted against the right person.' },
    ],
  },
  {
    title: 'Stock, buying and reports',
    icon: '📊',
    items: [
      { name: 'Fuel and oil stock', text: 'Stock goes down as you sell and goes up as you buy.' },
      { name: 'Buying from suppliers', text: 'Record every purchase and print the slip.' },
      { name: 'Daily expenses', text: 'Write down what you spend so you see the real picture.' },
      { name: 'Dashboard', text: 'See sales, credit and collections for the day at a glance.' },
      { name: 'Excel reports', text: 'Download a shift-wise report for your accountant.' },
    ],
  },
];

export const FUEL_FAQS = [
  { question: 'Who is Effy Fuel for?', answer: 'Effy Fuel is made for petrol bunk owners and managers who want to close shifts quickly, keep track of credit customers and always know their stock.' },
  { question: 'Do I need to install anything?', answer: 'No. Effy Fuel runs in your browser on a phone, tablet or computer. Sign up and start entering your shifts.' },
  { question: 'Can I use it on my phone?', answer: 'Yes. Effy Fuel works on phones, tablets and computers, so you can check your bunk from anywhere.' },
  { question: 'Can I give my credit customers a statement?', answer: 'Yes. You can print a statement for any customer with your logo and your UPI or bank details on it.' },
];
